/**
 * Constantes, mapeamentos e helpers compartilhados da tela de Follow-up.
 *
 * Toda tradução de jargão técnico do backend → linguagem humanizada vive aqui,
 * para garantir consistência entre cards, chips, badges e itens da fila.
 * Os termos técnicos ("triggerType", "INACTIVE_LEAD", "opted_out", "enrollment")
 * NUNCA são exibidos ao usuário — só usados como chaves internas.
 */

import axios from "axios";

// ─── Tipos de gatilho (grupos fixos) ────────────────────────────────────────
export const TRIGGER_TYPES = {
  LEAD: "INACTIVE_LEAD",
  CUSTOMER: "INACTIVE_CUSTOMER",
};

/**
 * Configuração de cada grupo fixo da aba "Minhas Cadências".
 * A ordem do array define a ordem de exibição na tela.
 */
export const SEQUENCE_GROUPS = [
  {
    triggerType: TRIGGER_TYPES.LEAD,
    icon: "💬",
    title: "Para quem conversou mas não agendou",
    description:
      "Acionada automaticamente quando alguém manda mensagem no WhatsApp mas não agenda nada.",
  },
  {
    triggerType: TRIGGER_TYPES.CUSTOMER,
    icon: "🔁",
    title: "Para clientes que não voltam",
    description:
      "Acionada automaticamente quando um cliente não agenda há mais de 30 dias.",
  },
];

// ─── Status de enrollment → labels humanizados ──────────────────────────────
// `chip` indica se o status aparece como filtro de chip na aba "Quem está na fila".
export const ENROLLMENT_STATUS = {
  active: {
    label: "Enviando mensagens",
    chipLabel: "Enviando",
    icon: "💬",
    tone: "blue",
    chip: true,
  },
  converted: {
    label: "Voltou!",
    chipLabel: "Convertidos",
    icon: "🎉",
    tone: "green",
    chip: true,
  },
  paused: {
    label: "Pausado",
    chipLabel: "Pausados",
    icon: "⏸",
    tone: "amber",
    chip: true,
  },
  completed: {
    label: "Concluído (sem retorno)",
    chipLabel: "Concluídos",
    icon: "✅",
    tone: "gray",
    chip: true,
  },
  opted_out: {
    label: "Desistiu",
    chipLabel: "Desistiram",
    icon: "🚫",
    tone: "redSoft",
    chip: true,
  },
  failed: {
    label: "Erro de envio",
    chipLabel: "Com erro",
    icon: "❌",
    tone: "red",
    chip: false,
  },
};

/** Mapeamento dos cards de métrica do topo → chave de `enrollmentsByStatus`. */
export const METRIC_CARDS = [
  { key: "active", icon: "📬", label: "Enviando mensagens" },
  { key: "converted", icon: "🎉", label: "Voltaram" },
  { key: "paused", icon: "⏸", label: "Pausados" },
  { key: "completed", icon: "✅", label: "Concluídos" },
];

// ─── Variáveis disponíveis nos templates de mensagem ────────────────────────
export const TEMPLATE_VARIABLES = [
  { token: "{{clientName}}", example: "João Silva", desc: "nome do cliente" },
  {
    token: "{{serviceName}}",
    example: "Corte de Cabelo",
    desc: "nome do serviço",
  },
  {
    token: "{{tenantName}}",
    example: "Barbearia do Zé",
    desc: "nome do seu negócio",
  },
];

export const VARIABLES_HINT =
  "💡 Variáveis: {{clientName}} · {{serviceName}} · {{tenantName}}";

// Limites de validação (espelham o que o backend aceita).
export const MIN_MESSAGE_LENGTH = 10;
export const MIN_OFFSET_DAYS = 1;
export const ENROLLMENTS_PAGE_SIZE = 20;

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Detecta cancelamento de request (AbortController.abort) — tratado como no-op. */
export function isCanceledError(err) {
  return (
    axios.isCancel?.(err) ||
    err?.code === "ERR_CANCELED" ||
    err?.name === "CanceledError" ||
    err?.name === "AbortError"
  );
}

// Códigos de 403 de plano insuficiente aceitos. "FEATURE_NOT_INCLUDED" é o
// código REAL emitido pelo planMiddleware (caso ai_advanced); "plan_limit_reached"
// é mantido por compat caso o backend evolua.
const PLAN_LIMIT_CODES = ["FEATURE_NOT_INCLUDED", "plan_limit_reached"];

/** Detecta 403 de plano insuficiente vindo do backend (defesa em profundidade). */
export function isPlanLimitError(err) {
  return (
    err?.response?.status === 403 &&
    PLAN_LIMIT_CODES.includes(err?.response?.data?.code)
  );
}

/**
 * Calcula os dias acumulados a partir dos offsets relativos de cada step.
 * Ex.: offsets [7, 7, 16] → [7, 14, 30]. O backend guarda offset relativo ao
 * step anterior; a UI exibe o "Dia X" absoluto desde a entrada na cadência.
 */
export function cumulativeDays(steps = []) {
  let acc = 0;
  return steps.map((s) => {
    acc += Number(s?.offsetDays ?? 0);
    return acc;
  });
}

/** Substitui as variáveis do template por valores de exemplo (preview). */
export function previewMessage(template = "") {
  let out = template;
  for (const v of TEMPLATE_VARIABLES) {
    out = out.split(v.token).join(v.example);
  }
  return out;
}

/**
 * Formata telefone BR a partir de dígitos crus.
 * Suporta 10 (fixo) e 11 (celular) dígitos; com DDI 55 também é tolerado.
 * Telefones fora do padrão são devolvidos como vieram (sem quebrar a UI).
 */
export function formatPhoneBR(raw) {
  if (!raw) return "";
  let digits = String(raw).replace(/\D/g, "");
  // Remove DDI 55 quando presente (ex.: 5551999998888).
  if (digits.length === 13 && digits.startsWith("55")) {
    digits = digits.slice(2);
  }
  if (digits.length === 12 && digits.startsWith("55")) {
    digits = digits.slice(2);
  }
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return String(raw);
}

/**
 * Calcula o tempo restante até `nextSendAt` em linguagem humanizada.
 * Retorna { soon: boolean, text: string }. `soon` indica que a data já passou
 * (mensagem prestes a ser enviada pelo job — não é erro).
 */
export function describeNextSend(nextSendAt, now = Date.now()) {
  if (!nextSendAt) return { soon: false, text: "Sem próxima mensagem agendada" };
  const target = new Date(nextSendAt).getTime();
  if (Number.isNaN(target)) {
    return { soon: false, text: "Sem próxima mensagem agendada" };
  }
  const diffMs = target - now;
  if (diffMs <= 0) {
    return { soon: true, text: "Mensagem a caminho..." };
  }
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (days <= 1) {
    const hours = Math.ceil(diffMs / (1000 * 60 * 60));
    if (hours <= 1) return { soon: false, text: "Próxima mensagem em menos de 1h" };
    return { soon: false, text: `Próxima mensagem em ${hours}h` };
  }
  return { soon: false, text: `Próxima mensagem em ${days} dias` };
}

/** Dias decorridos desde uma data (ex.: "Entrou há 11 dias"). */
export function daysSince(dateStr, now = Date.now()) {
  if (!dateStr) return null;
  const start = new Date(dateStr).getTime();
  if (Number.isNaN(start)) return null;
  const diffMs = now - start;
  if (diffMs < 0) return 0;
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

/** Texto "Entrou há X dias / hoje / ontem". */
export function describeEnrolledAt(enrolledAt, now = Date.now()) {
  const d = daysSince(enrolledAt, now);
  if (d === null) return "";
  if (d === 0) return "Entrou hoje";
  if (d === 1) return "Entrou ontem";
  return `Entrou há ${d} dias`;
}
